import AnimateIn from "@/components/AnimateIn";
import ManifestoHero from "@/components/guatemala/ManifestoHero";
import Countdown from "@/components/guatemala/Countdown";
import SealedEnvelope from "@/components/guatemala/SealedEnvelope";

// Each declaration becomes its own full-viewport frame — slow scroll reveals them
// one line at a time. Statement-driven sibling to Guatemala's image-driven parallax.
const declarations = [
  {
    label: "01 — SCHOOL",
    line: "YOU WERE TAUGHT TO FOLLOW INSTRUCTIONS.",
    under: "Not to question where they came from.",
  },
  {
    label: "02 — WORK",
    line: "YOU WERE TAUGHT TO GET A JOB.",
    under: "Not to ask who benefits from the answer.",
  },
  {
    label: "03 — NEWS",
    line: "YOU WERE SHOWN WHAT KEPT YOU WATCHING.",
    under: "Not what kept you informed.",
  },
  {
    label: "04 — MONEY",
    line: "YOU WERE PROMISED HARD WORK WAS ENOUGH.",
    under: "Then they moved the goalposts.",
  },
  {
    label: "05 — VOTING",
    line: "YOU WERE HANDED THE SMALLEST VERSION OF POWER.",
    under: "And told it was the biggest.",
  },
];

const facts = [
  {
    num: "01",
    text: "The education system was designed to produce workers. Not thinkers.",
  },
  {
    num: "02",
    text: "The news does not exist to inform you. It exists to retain you. Fear and outrage are the most effective retention tools ever discovered.",
  },
  {
    num: "03",
    text: "The wealth gap is not a malfunction of the economic system. It is the intended outcome.",
  },
  {
    num: "04",
    text: "Voting is the smallest possible version of civic power. It was designed to feel like the biggest.",
  },
  {
    num: "05",
    text: "The conception that hard work alone is enough has kept people working harder inside a system that was never going to give them what it promised.",
  },
];

// System Drop date — May 31, 2026 UTC
export const systemDropDate = "2026-05-31T00:00:00Z";

export default function SystemDrop() {
  return (
    <div className="bg-black" data-testid="system-page">
      {/* 1. MANIFESTO HERO */}
      <ManifestoHero
        sublabel="DOMESTIC VOL. 1 — WC × THE SYSTEM"
        lines={[
          "THEY BUILT THE SYSTEM.",
          "YOU WERE TOLD IT WAS FOR YOU.",
          "IT NEVER WAS.",
        ]}
        redWord="NEVER"
        ctaHref="#system-signup"
      />

      {/* 2. COUNTDOWN */}
      <section
        className="relative py-28 md:py-36 px-6 md:px-12 overflow-hidden border-t border-white/10"
        data-testid="system-countdown"
      >
        <div className="max-w-5xl mx-auto">
          <AnimateIn variant="fade">
            <div className="wc-label text-[10px] md:text-xs gt-red text-center tracking-[0.4em] mb-10">
              UNTIL THE SEAL BREAKS
            </div>
          </AnimateIn>
          <AnimateIn variant="fade" delay={100}>
            <Countdown
              target={systemDropDate}
              caption="DOMESTIC VOL. 1 — DROPS MAY 31, 2026"
              testid="system-countdown-timer"
            />
          </AnimateIn>
        </div>
      </section>

      {/* 3. DECLARATIONS — statement-driven parallax sibling */}
      <div className="border-t border-white/10">
        {declarations.map((d, i) => (
          <DeclarationPanel key={i} index={i} {...d} />
        ))}
      </div>

      {/* 4. THE PIVOT — volta */}
      <section
        className="relative px-6 md:px-12 py-40 border-t border-white/10"
        data-testid="system-pivot"
      >
        <div className="max-w-5xl mx-auto text-center">
          <AnimateIn variant="fade">
            <div className="wc-label text-[10px] gt-red tracking-[0.4em] mb-10">
              THE WRONG CONCEPTION
            </div>
          </AnimateIn>
          <AnimateIn variant="fade" delay={150}>
            <h2 className="wc-heading text-white text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-widest">
              IT WAS NEVER BUILT TO GIVE YOU FREEDOM.
            </h2>
          </AnimateIn>
          <AnimateIn variant="fade" delay={350}>
            <h2 className="wc-heading text-white text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-widest mt-6 md:mt-10">
              IT WAS BUILT TO KEEP YOU <span className="gt-red">COMPLIANT</span>.
            </h2>
          </AnimateIn>
          <AnimateIn variant="fade" delay={550}>
            <p className="text-white/60 text-base md:text-lg mt-14 max-w-2xl mx-auto leading-[1.9]">
              Those are very different things. This drop is for the ones who finally started questioning which one they have been living inside.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* 5. WHAT THEY NEVER TOLD YOU */}
      <section
        className="relative px-6 md:px-12 py-32 border-t border-white/10"
        data-testid="system-facts"
      >
        <div className="max-w-5xl mx-auto">
          <AnimateIn variant="fade">
            <h2 className="wc-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-widest">
              WHAT THEY NEVER TOLD YOU
            </h2>
          </AnimateIn>
          <div className="mt-20 space-y-14">
            {facts.map((f) => (
              <AnimateIn
                key={f.num}
                variant="fade"
                className="relative overflow-hidden py-10 border-t border-white/10"
              >
                <div
                  className="wc-heading absolute -top-4 -right-2 text-[10rem] md:text-[16rem] text-white/[0.04] leading-none pointer-events-none select-none"
                  aria-hidden
                >
                  {f.num}
                </div>
                <div className="relative z-10 flex gap-6 md:gap-10 items-start">
                  <span className="wc-heading text-white/40 text-3xl md:text-4xl">
                    {f.num}
                  </span>
                  <p className="text-white text-lg md:text-2xl leading-[1.45] pt-1 max-w-3xl">
                    {f.text}
                  </p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* 6. COLLECTION TEASER — minimal, statement only */}
      <section
        className="relative px-6 md:px-12 py-40 border-t border-white/10 text-center"
        data-testid="system-collection"
      >
        <AnimateIn variant="fade">
          <div className="wc-label text-[10px] gt-red tracking-[0.4em] mb-10">
            THE COLLECTION
          </div>
        </AnimateIn>
        <AnimateIn variant="fade" delay={150}>
          <h2 className="wc-heading text-white text-5xl md:text-7xl lg:text-8xl tracking-widest leading-[0.95]">
            BUILT FOR QUESTIONING.
            <br />
            NOT COMPLIANCE.
          </h2>
        </AnimateIn>
        <AnimateIn variant="fade" delay={300}>
          <p className="text-white/60 mt-12 max-w-xl mx-auto text-base md:text-lg leading-[1.8]">
            No guaranteed restock. If you miss it, you miss it.
          </p>
        </AnimateIn>
      </section>

      {/* 7. SEALED ENVELOPE WAITLIST */}
      <SealedEnvelope
        id="system-signup"
        sealLabel="DOMESTIC VOL. 1 — DROPS MAY 31, 2026"
        openedLabel="SEAL BROKEN"
        stampText="WC / DOMESTIC VOL. 1"
        coords="THE SYSTEM"
        heading="BREAK THE SEAL"
        body="The window opens May 31. Take your place in line. You'll be the first to know when it drops."
        openedHeading="YOU'RE ON THE LIST"
        openedBody="Drop your email. First access before anyone else. No guaranteed restock."
        source="system"
        testidPrefix="system-signup"
      />
    </div>
  );
}

function DeclarationPanel({ label, line, under, index }) {
  return (
    <section
      className="relative min-h-[80vh] flex items-center px-6 md:px-16 py-28 border-t border-white/10 bg-black"
      data-testid={`system-declaration-${index}`}
    >
      <div className="absolute inset-0 gt-grain opacity-20 pointer-events-none" />
      <div className="relative z-10 max-w-5xl w-full">
        <AnimateIn variant="fade">
          <div className="wc-label text-[10px] md:text-xs gt-red tracking-[0.4em] mb-8">
            {label}
          </div>
        </AnimateIn>
        <AnimateIn variant="fade" delay={150}>
          <h3 className="wc-heading text-white text-3xl md:text-5xl lg:text-7xl leading-[1.05] tracking-widest">
            {line}
          </h3>
        </AnimateIn>
        <AnimateIn variant="fade" delay={350}>
          <p className="text-white/55 text-lg md:text-2xl mt-10 max-w-2xl leading-[1.5]">
            {under}
          </p>
        </AnimateIn>
      </div>
    </section>
  );
}
